import React, { useState } from 'react';
import {
  Alert, Button, CircularProgress, Snackbar, Tooltip,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import saveFile from './saveFile';

// Any table on screen, as a spreadsheet.
//
// The registers have been downloadable for a while and everything else was not, which made the
// answer to "send me last week's trips" a screenshot, or a request to somebody with database
// access. The same rule the registers already follow applies everywhere: a table that is worth
// putting on screen is worth taking away.
//
// Two things are deliberate, and both come from the registers.
//
// What is written is what the person is looking at. A search, a filter or a date range has already
// narrowed the rows, and the file holds exactly those, in the order they are on screen - because a
// file that quietly holds more than the screen did is one nobody can reconcile against what they
// saw. It follows that this takes the rows the page has already prepared rather than fetching its
// own, which also means the button cannot disagree with the table beside it.
//
// And the columns are the ones on screen rather than the fields underneath them. These tables are
// read by people who recognise a plate and a carrier's name, not an id, so a column exports what the
// cell shows - a formatted date, a resolved name, a percentage - and ids appear only where somebody
// might have to match a row back to another system.

/**
 * Writes the rows as a workbook carrying this workspace's own name and logo, set up to print.
 *
 * `columns` is a list of [heading, read] pairs, where `read` is given the row and returns what the
 * cell shows. A pair rather than a key so a column can format, resolve or derive, exactly as the
 * table does.
 *
 * The cells are read here and sent as text, which keeps the one rule these downloads have always
 * followed: the file holds what the screen held. The server decides which whole columns are numbers
 * and types them so they can be summed, and leaves everything else exactly as it was shown.
 *
 * `title` is what goes at the top of the sheet and on every printed page. Falls back to the file
 * name so a caller that has not been given one still produces something a person can read.
 */
export const downloadTable = async (name, columns, rows, title) => {
  const response = await fetch('/api/operations/exports/table', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      title: title || name.replace(/-/g, ' ').replace(/^./, (ch) => ch.toUpperCase()),
      columns: columns.map(([heading]) => heading),
      rows: rows.map((row, index) => columns.map(([, read]) => {
        const value = read(row, index);
        return value === null || value === undefined ? '' : String(value);
      })),
    }),
  });
  if (!response.ok) {
    const said = await response.json().catch(() => null);
    throw new Error(said?.message || 'The download could not be prepared.');
  }
  return saveFile(`${name}.xlsx`, await response.blob());
};

/**
 * The button that does it, so every table offers the same thing in the same words.
 *
 * Disabled rather than hidden when there is nothing to write: a button that comes and goes as a
 * filter empties the table reads as a fault, and somebody who has filtered down to nothing is
 * better told that than left looking for the control they saw a moment ago.
 */
const DownloadTable = ({
  name, columns, rows, label = 'Download', disabled = false, title, ...rest
}) => {
  const empty = !rows || !rows.length;
  // The workbook is built by the server now, so this takes a moment and can fail. Both are said
  // out loud: a button that does nothing for two seconds reads as broken, and a download that
  // failed silently is worse than one that explains itself.
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const run = async () => {
    setWorking(true); setError('');
    try { await downloadTable(name, columns, rows, title); } catch (e) { setError(e.message); } finally { setWorking(false); }
  };
  return (
    <>
      <Tooltip title={empty ? 'Nothing to download in this view' : 'Download what is on screen as a printable spreadsheet'}>
        {/* A disabled button fires no events, so the tooltip needs an element of its own to sit on. */}
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={working ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
            disabled={disabled || empty || working}
            onClick={run}
            {...rest}
          >
            {working ? 'Preparing…' : label}
          </Button>
        </span>
      </Tooltip>
      <Snackbar open={!!error} autoHideDuration={8000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </>
  );
};

export default DownloadTable;
